import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../context/PermissionContext";
import { useTopNavMenu } from "../context/TopNavMenuContext";
import { getModuleRegistryTree, isSubGroup, ModuleEntry, ModuleSubGroup } from "../constants/modules";
import TopNavLink from "./TopNavLink";

const defsGroup = getModuleRegistryTree().find((g) => g.key === "definitions");

function findSubGroup(key: string): ModuleSubGroup | undefined {
  if (!defsGroup) return undefined;
  const found = defsGroup.children.find((c) => isSubGroup(c) && c.key === key);
  return found && isSubGroup(found) ? found : undefined;
}

function navEntries(subGroup: ModuleSubGroup | undefined): ModuleEntry[] {
  if (!subGroup) return [];
  return subGroup.children.filter((entry) => !!entry.path) as ModuleEntry[];
}

const partnersSubGroup = findSubGroup("partners");
const itemsSubGroup = findSubGroup("items");
const programSubGroup = findSubGroup("program");

const partnerEntries = navEntries(partnersSubGroup);
const itemEntries = navEntries(itemsSubGroup);
const programEntries = navEntries(programSubGroup);

type Section = {
  key: string;
  label: string;
  entries: Array<{ key: string; path: string; label: string; adminOnly?: boolean }>;
};

const TopDefinitionsMenu: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasAccess, isAdmin } = usePermissions();
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const { activeMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose } = useTopNavMenu();
  const open = activeMenu === "definitions";

  const nav = (module: string) => hasAccess(module);
  const canUseEntry = (entry: { key: string; adminOnly?: boolean }) =>
    entry.adminOnly ? isAdmin || nav(entry.key) : nav(entry.key);
  const hasComputerSystemAccess = nav("DEVICES") || nav("PRINTERS") || nav("PHONES") || nav("COMPUTERS");

  const warehousesEntry = { key: "STOCK_MANAGEMENT", path: "/stock-management", label: t("sidebar.warehouses", "Складишта") };

  const mappedItemEntries = itemEntries
    .filter((entry) => entry.path)
    .map((entry) => ({ key: entry.key, path: entry.path as string, label: t(entry.labelKey), adminOnly: entry.adminOnly }));

  const itemSectionEntries = mappedItemEntries.flatMap((entry) =>
    entry.path === "/items" ? [entry, warehousesEntry] : [entry]
  );
  if (!itemSectionEntries.some((entry) => entry.key === "STOCK_MANAGEMENT")) {
    itemSectionEntries.unshift(warehousesEntry);
  }

  const sections: Section[] = [
    {
      key: "partners",
      label: t(partnersSubGroup ? partnersSubGroup.labelKey : "sidebar.partner"),
      entries: [
        ...partnerEntries
          .filter((entry) => entry.path)
          .map((entry) => ({ key: entry.key, path: entry.path as string, label: t(entry.labelKey), adminOnly: entry.adminOnly })),
        { key: "PRINT_CONTROL_PANEL", path: "/partners-control-panel", label: t("sidebar.printControlPanel", "Print") },
        { key: "PARTNERS", path: "/contracted-prices-control-panel", label: t("contractedPrices.title", "Agreed Price Lists") },
      ],
    },
    {
      key: "items",
      label: t(itemsSubGroup ? itemsSubGroup.labelKey : "tableHeaders.idents"),
      entries: [
        ...itemSectionEntries,
        { key: "PRINT_CONTROL_PANEL", path: "/items-control-panel", label: t("sidebar.printControlPanel", "Print") },
      ],
    },
    {
      key: "classification",
      label: t("tableHeaders.classification"),
      entries: [
        { key: "CLASSIFICATION", path: "/classification/production-department", label: t("sidebar.classificationTypes.productionDepartment") },
        { key: "CLASSIFICATION", path: "/classification/collection", label: t("sidebar.classificationTypes.collection") },
        { key: "CLASSIFICATION", path: "/classification/classification", label: t("sidebar.classificationTypes.classification") },
        { key: "CLASSIFICATION", path: "/classification/raw-material-type", label: t("sidebar.classificationTypes.rawMaterialType") },
        { key: "CLASSIFICATION", path: "/classification/pa-type", label: t("sidebar.classificationTypes.paType") },
        { key: "CLASSIFICATION", path: "/classification/sa-type", label: t("sidebar.classificationTypes.saType") },
        { key: "CLASSIFICATION", path: "/classification/mp-pp-type", label: t("sidebar.classificationTypes.mpPpType") },
        { key: "CLASSIFICATION", path: "/classification/partner-type", label: t("sidebar.classificationTypes.partnerType") },
        { key: "PRINT_CONTROL_PANEL", path: "/classification-control-panel", label: t("sidebar.printControlPanel", "Print") },
      ],
    },
    {
      key: "program",
      label: t(programSubGroup ? programSubGroup.labelKey : "sidebar.program"),
      entries: programEntries
        .filter((entry) => entry.path)
        .map((entry) => ({ key: entry.key, path: entry.path as string, label: t(entry.labelKey), adminOnly: entry.adminOnly })),
    },
    {
      key: "computerSystem",
      label: t("sidebar.computerSystem", "Компјутерски систем"),
      entries: hasComputerSystemAccess
        ? [
            { key: "DEVICES", path: "/computer-system/devices", label: t("sidebar.devices", "Devices") },
            { key: "COMPUTER_STOCK", path: "/computer-system/computer-stock", label: t("computerStock.title", "Computer Stock") },
            { key: "CREDENTIALS", path: "/computer-system/credentials", label: t("credentials.title", "Credentials") },
            { key: "IT_PROCEDURES", path: "/computer-system/it-procedures", label: t("itProcedures.title", "IT Procedures") },
            ...(nav("PRINT_CONTROL_PANEL")
              ? [{ key: "PRINT_CONTROL_PANEL", path: "/computer-system-control-panel", label: t("sidebar.printControlPanel", "Контролна табла") }]
              : []),
          ]
        : [],
    },
  ]
    .map((section) => ({
      ...section,
      entries: section.key === "computerSystem" ? section.entries : section.entries.filter(canUseEntry),
    }))
    .filter((section) => section.entries.length > 0);

  if (sections.length === 0) return null;

  const activeSection = sections.find((section) => section.key === (activeSectionKey || sections[0]?.key)) || sections[0];

  return (
    <div
      className="relative"
      onMouseEnter={() => openMenu("definitions")}
      onMouseLeave={closeMenuDelayed}
    >
      <button
        type="button"
        onClick={() => (open ? closeMenu() : openMenu("definitions"))}
        className="flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span>{t("sidebar.definitions", "Шифрарници")}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && activeSection && (
        <div
          className="absolute left-0 top-full mt-1 flex rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xl"
          onMouseEnter={cancelClose}
        >
          <div className="w-56 p-2">
            {sections.map((section) => {
              const active = section.key === activeSection.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onMouseEnter={() => setActiveSectionKey(section.key)}
                  onClick={() => setActiveSectionKey(section.key)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors ${
                    active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                >
                  <span>{section.label}</span>
                  <ChevronRight size={15} />
                </button>
              );
            })}
          </div>

          <div className="w-72 border-l border-gray-200 p-2">
            <div className="space-y-1">
              {activeSection.entries.map((entry) => {
                const active = location.pathname === entry.path;
                return (
                  <TopNavLink
                    key={`${activeSection.key}-${entry.path}`}
                    to={entry.path}
                    label={entry.label}
                    moduleKey={entry.key}
                    isActive={active}
                    onClick={closeMenu}
                    className={`block truncate rounded px-2 py-1.5 text-xs transition-colors ${
                      active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopDefinitionsMenu;
